import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const RIGHTS = { access: "Access your data", correction: "Correct your data", deletion: "Delete your data", portability: "Export your data", restriction: "Restrict processing", objection: "Object to processing",
  withdraw_consent: "Withdraw consent", automated_decision_review: "Review of automated decisions", opt_out_sale_sharing: "Opt out of sale/sharing", opt_out_targeted_ads: "Opt out of targeted ads", limit_sensitive: "Limit sensitive data use", complaint: "Make a privacy complaint" };
export const REQUEST_STATUSES = ["received", "verifying", "in_progress", "completed", "refused"];
export const OPEN_STATUSES = ["received", "verifying", "in_progress"];
const STYLE = { received: "bg-sky-50 text-sky-800 border-sky-200", verifying: "bg-amber-50 text-amber-800 border-amber-200", in_progress: "bg-amber-50 text-amber-800 border-amber-200",
  completed: "bg-emerald-50 text-emerald-800 border-emerald-200", refused: "bg-rose-50 text-rose-800 border-rose-200", ready: "bg-emerald-50 text-emerald-800 border-emerald-200",
  preparing: "bg-amber-50 text-amber-800 border-amber-200", expired: "bg-stone-100 text-stone-600 border-stone-300", failed: "bg-rose-50 text-rose-800 border-rose-200" };

export const StatusPill = ({ status, testid }) => (
  <span data-testid={testid} className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold capitalize ${STYLE[status] || "border-stone-300 bg-stone-100 text-stone-700"}`}>{String(status).replace(/_/g, " ")}</span>
);

const RequestList = ({ rows }) => (
  <ul className="divide-y divide-border" data-testid="rights-request-list">
    {rows.map((r) => (
      <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-3 text-sm" data-testid={`rights-request-${r.id}`}>
        <span className="font-medium">{RIGHTS[r.type] || r.type}</span><StatusPill status={r.status} testid={`rights-status-${r.id}`} />
        <span className="text-xs text-slate-500">Sent {fmtDate(r.created_at)} · Reply due by {fmtDate(r.due_at)}</span>
        {r.response && <p className="w-full rounded-lg bg-emerald-50/70 p-2.5 text-xs text-slate-700" data-testid={`rights-response-${r.id}`}>Reply from Middle: {r.response}</p>}
      </li>))}
  </ul>
);

export const RightsRequests = ({ rights, days }) => {
  const [rows, setRows] = useState([]);
  const [type, setType] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/privacy/requests").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    setBusy(true);
    try { await api.post("/privacy/requests", { type, details }); toast.success("Request sent to the privacy team"); setType(""); setDetails(""); load(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <section className="surface space-y-4 p-5 sm:p-6" data-testid="privacy-rights">
      <div><h2 className="text-xl">Your rights where you are</h2><p className="text-xs text-slate-500">Send a request and our privacy team will reply within {days} days. You can follow its progress here.</p></div>
      <ul className="grid gap-1 text-sm text-slate-700 sm:grid-cols-2">{rights.map((r) => <li key={r}>• {RIGHTS[r] || r}</li>)}</ul>
      <div className="space-y-2 rounded-xl bg-[#F5F1EB] p-4">
        <Select value={type} onValueChange={setType}><SelectTrigger data-testid="rights-type-select" className="bg-white"><SelectValue placeholder="Choose what you'd like to do" /></SelectTrigger>
          <SelectContent>{rights.map((r) => <SelectItem key={r} value={r} data-testid={`rights-type-${r}`}>{RIGHTS[r] || r}</SelectItem>)}</SelectContent></Select>
        <Textarea data-testid="rights-details-input" className="bg-white" rows={3} maxLength={2000} value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Tell us what you need (optional). For a correction, say what's wrong and what it should be." />
        <div className="flex justify-end"><Button data-testid="rights-submit-btn" className="rounded-full px-6" disabled={!type || busy} onClick={submit}>{busy ? "Sending…" : "Send request"}</Button></div>
      </div>
      {rows.length > 0 && <RequestList rows={rows} />}
    </section>
  );
};
